import * as ImageConfig from '@/config/assets/image'
import { log } from '@/log'
import ApiBrand from '@/models/api/brand'
import { ApiProduct } from '@/models/api/product'
import { ApiProductclass } from '@/models/api/productclass'
import BehaviorConfig from '@/models/app-config/behavior/behavior'
import * as SKUFactory from '@/models/item/sku/factory/sku'
import { notEvaluatedSameDayShipping } from '@/models/item/sku/same-day-shipping'
import { ApiClient } from '@/services/api/client'
import ProductclassService from '@/services/api/productclass'
import GetStockStatus from '@/usecase/get-stock-status'
import { HOST_ID } from '@/util/appid'
import { CartItem } from '@spa/store/modules/cart/types'
import { ReasonForCannotBePurchasedList } from '@spa/store/modules/cart/types/reason-for-cannot-be-purchased'
import * as Parallel from 'async-parallel'
import { Either, Left, Right } from 'fp-ts/lib/Either'
import _ from 'lodash'
import { Response } from 'superagent'

/**
 * カートレコード commerce_user_cart に productclass の情報を inject して CartItem を作る
 * これは注文完了画面 OrderCompleteById で利用されることを想定している
 *
 * そもそも注文完了画面が CartItem に依存するべきかという議論もあるため、
 * 独立したファイルに切り出している
 * @see https://github.com/Everforth/growthpalette-front/issues/8868#issuecomment-3919979484
 *
 * @deprecated OrderCompleteById以外での利用は想定していない
 */
export class CreateCartItemsThroughInjectProductclass {
  private legacyCartJoinService: LegacyCartJoinService

  constructor() {
    this.legacyCartJoinService = new LegacyCartJoinService()
  }

  async convert(rawDataList: any[]): Promise<CartItem[]> {
    const converted = this.convertToCartItems(rawDataList)
    const joined = await this.legacyCartJoinService.join(rawDataList)

    return this.injectProductclassData(converted, joined.filter(i => i.fku))
  }

  private convertToCartItems(responseData: any[]): CartItem[] {
    return _.map(responseData, rawItem => this.convertToCartItem(rawItem))
  }

  private convertToCartItem(rawItem: any): CartItem {
    const apiProduct = new ApiProduct(_.get(rawItem, 'product'))
    const belongingBrand = new ApiBrand(rawItem.brands[0])

    return {
      id: rawItem.id,
      /**
       * この時点ではROOT商品情報が取得できていないので、一旦デフォルト値として""で埋める.
       * ROOT商品情報の取得に成功すれば、 #injectProdcutclassData 内部で置換される.
       * デフォルト値だった場合にどう表現するかは、UIコンポーネントに任せる.
       *
       * @see https://github.com/my-color/front/issues/4811#issuecomment-813854920
       * @see RawItemConverter.injectProductclassData
       */
      name: '',
      createdAt: _.get(rawItem, 'created_at'),
      unitPrice: _.get(rawItem, 'unitPrice'),
      /**
       * TODO: 一元管理する
       * 現状で @/models/cart/repository/impl/cart-item/engine-cart-item-repository にもベタ書きされている
       */
      isWithPaidMembershipPrice: _.get(rawItem, 'aux_string_1') === 'with-paid-member-price',
      skuSellPrice: apiProduct.price.regular,
      skuSalePrice: apiProduct.price.discounted,
      unitCount: _.get(rawItem, 'unitCount') as number,
      brandId: belongingBrand.id,
      brandName: belongingBrand ? belongingBrand.name : null,
      brandEnglishName: belongingBrand ? belongingBrand.englishName : null,
      skuId: apiProduct.id,
      skuShortId: apiProduct.shortId,
      skuNumber: apiProduct.skuNumber,
      fkuId: apiProduct.productClassId,
      size: apiProduct.getSize(),
      sizeTagPathname: apiProduct.getSizeTagPathname(),
      onSale: apiProduct.isOnDiscountSale,
      salesStatus: _.get(rawItem, 'product.sales_status') as number,
      salesStartAt: apiProduct.regularSaleStartAt,
      salesEndAt: apiProduct.regularSaleEndAt,
      /**
       * cartItem.product には在庫情報が乗ってこない.
       *
       * なので、#injectProductclassData で対応するProductの情報を取得し、
       * そちらで予約商品かの情報を取得/上書きしている.
       */
      isPreSales: false,
      isNovelty: false,
      /**
       * 現状、FKUが取得できなかったなら、ROOT,FKU非公開、という判定をしている。
       * したがってこの情報は #injectProductclassData で決まる。
       * https://github.com/my-color/front/pull/2638#issuecomment-542615347
       */
      isFkuPublic: false,
      ineligibleForCoupon: false,
      freeShipping: false,
      categories: [],
      reasonForCannotBePurchased: ReasonForCannotBePurchasedList.asEmpty(),
      sameDayShipping: notEvaluatedSameDayShipping,
    }
  }

  private injectProductclassData(items: CartItem[], joined: JoinedProductclassData[]) {
    // Inject with joined product/productclass info
    _.map(items, (item) => {
      const entry = _.find(joined, ['id', item.id])
      if (entry === undefined) {
        log.warn('ROOT or FKU is not public, so there is no other way to assume detail of cart. treat it as unpublic.')

        item.isFkuPublic = false

        return item
      }
      item.isFkuPublic = true

      /**
       * FIXME:
       * 非常に場当たり的な対応だが、整理しようとするとおよそカート内のデータ構造を全て刷新する勢いの対応が必要そう.
       * 「カート内商品」という概念が、「ROOT/FKU/SKU」といった通常の「商品」とどういった関連に有るのか不明瞭.
       *
       * SKUが最小単位である以上、「カート内商品」の骨子となるのはSKUのはず.
       * ROOT/FKUの情報は、表示の都合のための付帯情報に過ぎないと思われる.
       * が、現状↑を表現できるだけの知識も表現力も現行のドメインオブジェクトは持ち合わせていないので、
       * 一旦「在庫薄取得」という機能のみを、ピンポイントに応急処置的に挿入している.
       *
       * まず「カート内商品」という概念を整理する必要がある.
       * 「カート内商品とはなんぞや」が定義できないと、どこに何を定義するべきかも定まらず、
       * ロジックが再び各層に散逸することも阻止できない.
       */
      const root = entry.root || new ApiProductclass(undefined) // :pensive: 一先ず、既存の動きから変えない・・・
      const fku = new ApiProductclass(entry.fku)
      const sku = new ApiProduct(entry.sku)
      const skuEntity = SKUFactory.create(sku)
      const getStockStatus = GetStockStatus.createViaConfig(
        BehaviorConfig.createFromBrand(sku.belongingBrand)
      )

      item.salesStartAt = sku.regularSaleStartAt
      item.salesEndAt = sku.regularSaleEndAt

      item.name = root.productName

      item.root = root
      item.rootId = root.id
      item.rootShortId = root.shortId // used for link to item page

      const colorTag = fku.color
      // FIXME: potential null error!
      if (colorTag) {
        item.color = colorTag.title
        item.colorName = colorTag.name
      }
      // TODO: Move this dummy image logic into a domain model
      const thumbnail = fku.representativeImage ? fku.representativeImage.url : ImageConfig.dummyImagePath

      if (thumbnail) {
        item.thumbnail = thumbnail
      }

      const stock = getStockStatus.amount(skuEntity.stock)
      item.maxSellCount = Math.min(
        sku.maxSellCount,
        stock
      )
      item.rootPurchaseLimit = root.purchaseLimit
      item.stock = stock
      /*
       * TODO: purchase-count-limit-specification を使う形にすると同時に、↓の形に置き換える
       *  item.maxSellCount = sku.maxSellCount
       *  item.stock = getStockStatus.amount(skuEntity.stock)
       *  item.rootPurchaseLimit = root.purchaseLimit
       *
       * item.maxSellCount に予めすべてを詰め込むのではなく、フラットに配置して、
       * 評価は後から実行される仕様オブジェクトに任せる.
       */

      item.isPreSales = sku.isPreSale

      item.isCatalogType = root.tags.some(t => t.pathname === 'mycolor.product.catalog')
      item.isNovelty = root.isNovelty
      item.ineligibleForCoupon = root.tags.some(t => t.pathname === 'mycolor.product.ineligible.coupon')
      item.freeShipping = root.tags.some(t => t.pathname === 'mycolor.product.delivery.free_shipping')
      item.categories = root.categories
    })

    return items
  }
}

/**
 * @deprecated
 *
 * 外部決済からの注文完了画面表示 OrderCompleteById で CartItem を作成するために古い実装を復元したもの
 * @see https://github.com/Everforth/growthpalette-front/issues/8868#issuecomment-3919979484
 */
class LegacyCartJoinService {
  private readonly appId: string
  private readonly productclassService: ProductclassService

  constructor() {
    this.appId = HOST_ID
    this.productclassService = new ProductclassService(this.appId)
  }

  // TODO: Throttle simultaneous API requests number
  // TODO: Refactoring
  async join(rawItems: any[]): Promise<JoinedProductclassData[]> {
    // Extract id list
    const idList: Array<{ id: string, skuId: string, fkuId: string }> = _.map(rawItems, (item) => {
      return {
        id: item.id,
        skuId: _.get(item, 'product.id'),
        fkuId: _.get(item, 'product.product_class_id'),
      }
    })

    // Get Products
    const skuList = await this.getSkuList(idList)

    // Get FKU
    const fkuList = await this.getFkuList(idList)

    // Join
    const joined = _.map(idList, (entry) => {
      // sku/fkuが取れないこと自体は異常系. 今は作りの都合上optionalで代用してしまっている
      // https://github.com/my-color/front/pull/3072#discussion_r368895161
      const skuEntry = _.find(skuList, item => item.id === entry.id && !item.error)
      const fkuEntry = _.find(fkuList, item => item.id === entry.id && !item.error)

      return {
        id: entry.id,
        ...skuEntry && {
          sku: skuEntry.sku,
        },
        ...fkuEntry && {
          fku: fkuEntry.fku,
          root: fkuEntry.root,
        },
      }
    })

    log.debug({ service: `LegacyCartJoinService/join`, joined })

    return joined
  }

  // tslint:disable-next-line:max-line-length
  private async getSkuList(idList: Array<{ id: string, skuId: string}>): Promise<Array<{ id: string, sku?: any, error?: boolean }>> {
    // Get Products
    const skuList = await Parallel.map(idList, async (entry) => {
      // TODO: Use better way to filter non-existing SKU
      if (!entry.skuId) {
        return {
          id: entry.id,
        }
      }

      const result = await this.getSku(entry.skuId)

      if (result.isLeft()) {
        return {
          id: entry.id,
          error: true,
        }
      }

      const sku = result.value.body.data

      return {
        id: entry.id,
        sku,
      }
    })

    return skuList as Array<{ id: string, sku: any }>
  }

  // tslint:disable-next-line:max-line-length
  private async getFkuList(idList: Array<{ id: string, fkuId: string}>): Promise<Array<{ id: string, fku?: any, root: ApiProductclass, error?: boolean }>> {
    const fkuList = await Parallel.map(idList, async (entry) => {
      // TODO: Use better way to filter non-existing FKU
      if (!entry.fkuId) {
        return {
          id: entry.id,
        }
      }

      const result = await this.getFku(entry.fkuId)

      if (result.isLeft()) {
        return {
          id: entry.id,
          error: true,
        }
      }

      const fku = result.value.body.data

      const rootInfo = _.get(fku, 'belonging_to.0')
      // https://github.com/my-color/front/pull/3072#discussion_r368895506
      // 「レコードによって情報量が違う」「レコードによってリクエストの有無が違う」は混乱の元になると判断して、
      // FKU に含まれる ROOT の要約を使わず、ROOT を常に取得している.
      const rootResult = await this.productclassService.getRootItem(rootInfo.id)

      if (rootResult.isLeft()) {
        return {
          id: entry.id,
          error: true,
        }
      }

      // TODO: Check FKU tag
      return {
        id: entry.id,
        fku,
        root: rootResult.value,
        error: false,
      }
    })

    return fkuList as Array<{ id: string, fku: any, root: any }>
  }

  private async getSku(id: string): Promise<Either<Error, Response>> {
    try {
      const response = await ApiClient
      .get(`/api/common-proxy/${this.appId}/product2/${id}`)
      .query({
        with: 'stock~detail',
      })

      return new Right(response)
    } catch (error) {
      log.error({ error })

      return new Left(error)
    }
  }

  private async getFku(id: string, query: any = {}): Promise<Either<Error, Response>> {
    try {
      const response = await ApiClient
        /*
         * productclassないしFKU情報の取得は ProductclassService に集約しているが、
         * この箇所は一時的にやむを得ず、直接 productclass2 を使って良いものとする.
         *
         * 理由は下記.
         * - /item/fku はFKUとしてサービスが公開する情報を提供するAPIである.
         *   - そのため、子商品やサブスクリプション申し込み用商品といったリソースは公開の対象外である
         *   - しかしカート内商品構築には、上記商品の情報も必要である
         *   - そのため、特に制限が設けられることのない proxy を使って、直接取得させている
         *     - ただしこれは「そうせざるを得ない」という消極的な判断である
         * - 本来、これら「カート内商品を構築するためのデータ集め」はclientで行うべきことではない
         *   - 知識の集約的にも、実装の一元化的にも、サーバー内部で実装されてしかるべき
         *   - したがって、ここでの「productclassを直接収集」は、サーバー側実装がclientに流出していると評価
         *   - であるならば、ここの実装は最終的にはサーバー側へ移植されるべきもの
         *   - サーバー側に移植された後なら、任意の形式でEngine APIを利用できる
         *
         * 上記の通り、最終的にはここにある「カート内商品を構築するためのデータ集め」は全てサーバーに移植され、
         * clientからは一掃されるということが前提になっており、それまでの一時しのぎとして
         * proxy経由でproductclass2を使うものである.
         */
        .get(`/api/common-proxy/${this.appId}/productclass2/${id}`)
        .query({
          with: 'belonging_to,indexsummary',
          ...query,
        })

      return new Right(response)
    } catch (error) {
      log.error({ error })

      return new Left(error)
    }
  }
}

class JoinedProductclassData {
  id: string
  sku?: any
  fku?: any
  root?: ApiProductclass
}
